import {data, math} from "azure-maps-control";
import {GeomUtils} from "@esWebAPI/es/util/GeomUtils";
import {NavUtils} from "@esWebAPI/es/util/NavUtils";

import type {MapMouseEvent} from "azure-maps-control";
import type {AzureBaseMap} from "@esWebAPI/es/map/module/AzureBaseMap";
import type {AzureOverlay} from "@esWebAPI/es/map/module/AzureOverlay";
import type {LineString, MultiPolygon, Polygon, Position} from "geojson";

type EditAction = "NONE" | "UPDATE" | "REMOVE" | "INSERT";

type GeomVertexIndex = {
    geomIndex: number
    vertexIndex: number
}

type MidPointsIndexMapItem = {
    polygon: number,
    ring: number,
    vertex: number
}

type MidPointsIndexMap = {
    [key: string]: MidPointsIndexMapItem
}

export class EditPolygonHandler {

    private readonly baseMap: AzureBaseMap;
    private readonly overlay: AzureOverlay;
    private readonly polygonStyle: any;
    private readonly verticesStyle: any;

    private readonly _eventChannel: any;
    private _geom: Polygon|MultiPolygon
    private readonly geodesic: boolean;

    private overlayId: number = 0;
    private verticesOverlayId: number = 0;
    private midPointsOverlayId: number = 0;

    private dragging: boolean = false;
    private dragPoint: Position | null = null;
    private dragPointIndex: GeomVertexIndex = {vertexIndex: -1, geomIndex: -1};
    private screenPosition: number[] = [];

    private midPoints: Position[][] = [];
    private midPointsIndexMap: MidPointsIndexMap = {};
    private midPointsDragIndex: GeomVertexIndex = {vertexIndex: -1, geomIndex: -1};

    private readonly vertexIdentity = "edit-polygon-vertex";
    private readonly vertexMidpoint = "edit-polygon-midpoint";

    constructor(baseMapModule: AzureBaseMap, overlayModule: AzureOverlay, geom: Polygon|MultiPolygon, geodesic: boolean = false) {

        this.baseMap = baseMapModule;
        this.overlay = overlayModule;

        this.verticesStyle = {
            "$type": "SymbolStyle",
            "color": 4278190080,
            "size": 15,
            "symbolTypeId": 0
        };

        this.polygonStyle = {
            "$type": "PolygonStyle",
            "line": {
                "color": 4278190080,  //#000000
                "endCap": 1,
                "join": 1,
                "width": 2
            },
            "fillColor": 0x80F2C811, //#F2C811 at 50% opacity
        };

        this._eventChannel = es.util.createEventChannel({onDragStart:1, onDragEnd: 1, onGeomChanged: 1, onDestroy: 1}, "es.map.handler.EditPolygonHandler");

        this._geom = geom;

        this.geodesic = geodesic;

        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.onContextMenu = this.onContextMenu.bind(this);
        this.onZoomEnd = this.onZoomEnd.bind(this);

        this.baseMap.map.events.add("mousemove", this.onMouseMove);
        this.baseMap.map.events.add("mousedown", this.onMouseDown);
        this.baseMap.map.events.add("mouseup", this.onMouseUp);
        this.baseMap.map.events.add("contextmenu", this.onContextMenu);
        this.baseMap.map.events.add("zoomend", this.onZoomEnd);

        this.init();
    }

    private async init() {
        await this.loadSprites();
        const geom = this.geodesic ? GeomUtils.withGreatCircle(this._geom) : this._geom;
        this.overlayId = this.overlay.add(geom, this.polygonStyle);
        this.drawVertices();
        this.drawMidPoints();
    }


    private loadSprites(): Promise<any> {

        //Vertices
        let imageId = `es_${this.verticesStyle.symbolTypeId}_${this.verticesStyle.size}_${this.verticesStyle.color}`;
        let url = `${es.restUrl}/style?style=(%24type~%27SymbolStyle%27~symbolTypeId~${this.verticesStyle.symbolTypeId}~size~${this.verticesStyle.size}~color~${this.verticesStyle.color})&width=${this.verticesStyle.size}&height=${this.verticesStyle.size}`;
        const verticesDfd = this.baseMap.imageSpriteManager.add(imageId, url);

        //MidPoint
        const color = es.util.color.withTransparency(this.verticesStyle.color, 70)
        imageId = `es_${this.verticesStyle.symbolTypeId}_${this.verticesStyle.size}_${color}`;
        url = `${es.restUrl}/style?style=(%24type~%27SymbolStyle%27~symbolTypeId~${this.verticesStyle.symbolTypeId}~size~${this.verticesStyle.size}~color~${color})&width=${this.verticesStyle.size}&height=${this.verticesStyle.size}`;
        const midPointsDfd = this.baseMap.imageSpriteManager.add(imageId, url);

        return Promise.all([verticesDfd, midPointsDfd]);
    }

    private drawVertices() {

        let vertices: Position[] = [];
        if (this._geom.type === "Polygon") {
            vertices = GeomUtils.getVertices(this._geom) as Position[];
        }
        if (this._geom.type === "MultiPolygon") {
            vertices = (GeomUtils.getVertices(this._geom) as Position[][]).flatMap(p => p);
        }

        if (this.verticesOverlayId) {
            this.overlay.remove(this.verticesOverlayId);
        }

        const points = new data.MultiPoint(vertices);
        this.verticesOverlayId = this.overlay.add(points, this.verticesStyle, undefined, {type: this.vertexIdentity});
    }

    private extractMidPoints(geom: LineString|Polygon|MultiPolygon, callback: (midPoint: Position, polygonIndex: number, lineIndex: number) => void): void {
        const distanceLimit = (this.verticesStyle.size * 2) * (this.verticesStyle.size * 2);

        if (geom.type === "MultiPolygon") {
            const positions = GeomUtils.getVertices(geom) as Position[][];
            positions.forEach((polygonPositions, polygonIndex: number) => {
                GeomUtils.extractLines(polygonPositions).forEach((line: Position[], lineIndex: number) => {
                    const screenPntA = NavUtils.toPx(this.baseMap, line[0]);
                    const screenPntB = NavUtils.toPx(this.baseMap, line[1]);
                    const distanceSq = es.util.math.distSq(screenPntA[0], screenPntA[1], screenPntB[0], screenPntB[1]);
                    if (distanceSq > distanceLimit) {
                        callback(getMidPoint(line, this.geodesic), polygonIndex, lineIndex);
                    }
                });
            })
        }
        else {
            const positions = GeomUtils.getVertices(geom) as Position[];
            GeomUtils.extractLines(positions).forEach((line: Position[], lineIndex: number) => {
                const screenPntA = NavUtils.toPx(this.baseMap, line[0]);
                const screenPntB = NavUtils.toPx(this.baseMap, line[1]);
                const distanceSq = es.util.math.distSq(screenPntA[0], screenPntA[1], screenPntB[0], screenPntB[1]);
                if (distanceSq > distanceLimit) {
                    callback(getMidPoint(line, this.geodesic), 0, lineIndex);
                }
            });
        }
    }

    private drawMidPoints() {
        this.midPoints = [];
        this.midPointsIndexMap = {};

        const midPoints: Position[][] = this._geom.coordinates.map(c => []); // Ensure all midPoints entries are filled with at least empty array as extractMidPoints might not callback for all indexes...

        this.extractMidPoints(this._geom, (midPoint, polygonIndex, lineIndex) => {
            if(!midPoints[polygonIndex]){
                midPoints[polygonIndex] = [];
            }
            midPoints[polygonIndex].push(midPoint);
            const key = `${polygonIndex},${midPoint[0]},${midPoint[1]}`;
            this.midPointsIndexMap[key] = {
                polygon: polygonIndex,
                ring : 0,
                vertex : lineIndex
            };
        });
        this.midPoints = midPoints;

        let coordinates = midPoints.flatMap(p => p);

        if (this.midPointsOverlayId > 0) {
            this.overlay.update(this.midPointsOverlayId, new data.MultiPoint(coordinates));
        } else {
            const style = es.api.style.withColor(this.verticesStyle, es.util.color.withTransparency(this.verticesStyle.color, 70));
            this.midPointsOverlayId = this.overlay.add(new data.MultiPoint(coordinates), style, undefined, {type: this.vertexMidpoint});
        }
    }

    private onMouseMove(e: MapMouseEvent) {
        if(!e.pixel){
            return true;
        }

        if (this.dragging && e.pixel) {
            this.screenPosition = e.pixel;
            this.movePoint();
            return false;
        }

        const pixel = e.pixel as [number, number];
        this.dragPointIndex = getClosestVertexIndex(this.baseMap, this._geom, pixel, 10);

        if(this.dragPointIndex.vertexIndex !== -1){
            this.baseMap.setCursor("crosshair");
            this.dragPoint = this._geom.type == "Polygon" ? this._geom.coordinates[0][this.dragPointIndex.vertexIndex] :
                                                            this._geom.coordinates[this.dragPointIndex.geomIndex][0][this.dragPointIndex.vertexIndex];
            this.midPointsDragIndex = {vertexIndex: -1, geomIndex: -1};
            return false;
        }

        // No point in the vicinity. Check midPoints
        this.midPointsDragIndex = getClosesMidPointIndex(this.baseMap, this._geom, this.midPoints, pixel, 10);
        if(this.midPointsDragIndex.vertexIndex !== -1){
            this.dragPoint = null;
            this.baseMap.setCursor("crosshair");
            return false;
        }

        this.baseMap.setCursor("default");
        this.dragPoint = null;
        return true;
    }

    private onMouseDown(e: MapMouseEvent) {
        if(this.dragging || (this.dragPointIndex.vertexIndex == -1 && this.midPointsDragIndex.vertexIndex == -1) || (e.originalEvent as MouseEvent).button !== 0){
            return true;
        }

        this.dragging = true;
        this.baseMap.map.setUserInteraction({dragPanInteraction: false});
        return false;
    }

    private onMouseUp(e: MapMouseEvent) {
        if(!this.dragging || (e.originalEvent as MouseEvent).button !== 0){
            return true;
        }

        this.dragging = false;
        this.baseMap.map.setUserInteraction({dragPanInteraction: true});
        this._eventChannel.fireEvent("onDragEnd", $.extend(true, [], this._geom));
        this._eventChannel.fireEvent("onGeomChanged");
        return false;
    }

    private onContextMenu() {
        if (this.dragPoint) {
            if (this._geom.coordinates[0].length == 4) {
                // last coordinate is same as first, so, it actually has 3 points and we are trying to remove one
                es.alert.error($i18n("_Error_Remove_Vertex_Minimum"));
                return false;
            }
            this.updateGeom([], "REMOVE", this.dragPointIndex);
            this._eventChannel.fireEvent("onGeomChanged");
        }
        return false;
    }

    private onZoomEnd() {
        this.drawMidPoints();
    }

    private movePoint() {
        
        if(!this.dragging) {
            return;
        }

        const position = this.baseMap.fromPx(this.screenPosition);
        let updateIndex: GeomVertexIndex = {geomIndex: -1, vertexIndex: -1};
        let action: EditAction = "NONE";


        if (this.dragPoint) {
            updateIndex = this.dragPointIndex;
            // Moving a vertex
            if(this.midPointsDragIndex.vertexIndex > -1){
                const midPoint = this.midPoints[this.midPointsDragIndex.geomIndex][this.midPointsDragIndex.vertexIndex];
                const key = `${this.midPointsDragIndex.geomIndex},${midPoint[0]},${midPoint[1]}`
                updateIndex = {
                    geomIndex: this.midPointsIndexMap[key].polygon,
                    vertexIndex: this.midPointsIndexMap[key].vertex
                };
            }
            action = "UPDATE";
        } else if (this.midPointsDragIndex.vertexIndex > -1) {
            // Inserting a vertex
            action = "INSERT";
            const midPoint = this.midPoints[this.midPointsDragIndex.geomIndex][this.midPointsDragIndex.vertexIndex];
            const key = `${this.midPointsDragIndex.geomIndex},${midPoint[0]},${midPoint[1]}`
            const midPointIndex = this.midPointsIndexMap[key];
            updateIndex = {geomIndex: midPointIndex.polygon, vertexIndex:midPointIndex.vertex + 1};
            this.dragPointIndex = updateIndex;
            this.midPointsDragIndex.geomIndex = -1;
            this.midPointsDragIndex.vertexIndex = -1;
        }

        this.updateGeom(position, action, updateIndex);
        this.dragPoint = [position[0], position[1]];
    }

    /**
     * Update the geometry
     * @param newPoint New point (mercator coordinates).
     * @param action  Edit Action
     * @param updateIndex Index of the vertex to edit.
     */
    private updateGeom(newPoint: number[], action: EditAction, updateIndex: GeomVertexIndex): void{

        let vertices = GeomUtils.getVertices(this._geom);

        if (action === "UPDATE") {

            if (this._geom.type === "MultiPolygon") {
                if (updateIndex.vertexIndex === 0 || updateIndex.vertexIndex === vertices[updateIndex.geomIndex].length-1) {
                    vertices[updateIndex.geomIndex][0] = newPoint;
                    vertices[updateIndex.geomIndex][vertices[updateIndex.geomIndex].length-1] = newPoint;
                } else {
                    vertices[updateIndex.geomIndex][updateIndex.vertexIndex] = newPoint;
                }
            } else {

                if (updateIndex.vertexIndex === 0 || updateIndex.vertexIndex === vertices.length-1) {
                    vertices[0] = newPoint;
                    vertices[vertices.length-1] = newPoint;
                } else {
                    vertices[updateIndex.vertexIndex] = newPoint;
                }
            }
        }
        else if (action  === "INSERT") {
            if (this._geom.type === "MultiPolygon") {
                vertices[updateIndex.geomIndex].splice(updateIndex.vertexIndex, 0, newPoint);
            } else {
                vertices.splice(updateIndex.vertexIndex, 0, newPoint);
            }
        }
        else if (action  === "REMOVE") {

            if (this._geom.type === "MultiPolygon") {
                vertices[updateIndex.geomIndex].splice(updateIndex.vertexIndex, 1);
                const nbVertices = vertices[updateIndex.geomIndex].length;
                if (updateIndex.vertexIndex === 0) {
                    //1st point is removed, update the last vertex of the polygon
                    vertices[updateIndex.geomIndex][nbVertices - 1] = vertices[updateIndex.geomIndex][0];
                }
                if (updateIndex.vertexIndex === nbVertices - 1) {

                    vertices[updateIndex.geomIndex][0] = vertices[updateIndex.geomIndex][nbVertices - 1];
                }
            }
            else {
                vertices.splice(updateIndex.vertexIndex, 1);
                const nbVertices = vertices.length;
                if (updateIndex.vertexIndex === 0) {
                    //1st point is removed, update the last vertex of the polygon
                    vertices[nbVertices - 1] = vertices[0];
                }
                if (updateIndex.vertexIndex === nbVertices - 1) {
                    //Last point is removed, update the 1st vertex of the polygon
                    vertices[0] = vertices[nbVertices - 1];
                }
            }
        }
        else {
            throw new Error("Invalid edit action " + action);
        }

        let style;
        if (this._geom.type === "Polygon") {
            this._geom.coordinates[0] = vertices as Position[];
            style = this.polygonStyle;
        }
        else if (this._geom.type === "MultiPolygon") {
            this._geom.coordinates = [];
            (vertices as Position[][]).forEach((positions, i) => {
                this._geom.coordinates[i] = [positions];
            })

            style = this.polygonStyle;
        }
        else {
            throw new Error("Unsupported geometry: " + this._geom);
        }

        if (this.overlayId > 0) {
            this.overlay.remove(this.overlayId);
        }
        let geom = this.geodesic ? GeomUtils.withGreatCircle(this._geom) : this._geom;
        this.overlayId = this.overlay.add(geom, style);

        this.drawVertices();
        this.drawMidPoints();
    }

    public destroy() {

        this.baseMap.map.events.remove("mousemove", this.onMouseMove as any);
        this.baseMap.map.events.remove("mouseup", this.onMouseUp as any);
        this.baseMap.map.events.remove("mousedown", this.onMouseDown as any);
        this.baseMap.map.events.remove("contextmenu", this.onContextMenu as any);
        this.baseMap.map.events.remove("zoomend", this.onZoomEnd as any);

        this.overlay.remove(this.overlayId);
        this.overlay.remove(this.verticesOverlayId);
        this.overlay.remove(this.midPointsOverlayId);

        this.baseMap.setCursor("default");
        this._eventChannel.fireEvent("onDestroy");
        this._eventChannel.destroy();
    }

    get eventChannel() {
        return this._eventChannel;
    }

    get geom() {
        return this._geom;
    }

}

function getClosestVertexIndex(baseMap: AzureBaseMap, geom: Polygon|MultiPolygon, pixel: [number, number], pixelMargin: number): GeomVertexIndex {
    const position = baseMap.fromPx(pixel);
    const maxOffset = baseMap.fromPx([pixel[0] - pixelMargin, pixel[1] - pixelMargin]);
    const maxDx = Math.abs(position[0] - maxOffset[0]);
    const maxDy = Math.abs(position[1] - maxOffset[1]);

    let vertexIndex = -1;
    let geomIndex = -1;
    let minOffset = maxDy + maxDx;

    if (geom.type == "Polygon") {
        const coordinates = geom.coordinates[0];
        for (let i = 0; i < coordinates.length; i++) {
            const coord = coordinates[i];
            const dx = Math.abs(coord[0] - position[0]);
            const dy = Math.abs(coord[1] - position[1]);
            const offset = dy + dx;

            if (offset < minOffset) {
                minOffset = offset;
                vertexIndex = i;
            }
        }
        return {geomIndex: 0, vertexIndex: vertexIndex};
    }

    if (geom.type == "MultiPolygon") {
        for (let g = 0; g < geom.coordinates.length; g++) {
            const polygon = geom.coordinates[g];
            for (let i = 0; i < polygon[0].length; i++) {
                const coord = polygon[0][i];
                const dx = Math.abs(coord[0] - position[0]);
                const dy = Math.abs(coord[1] - position[1]);
                const offset = dy + dx;

                if (offset < minOffset) {
                    minOffset = offset;
                    vertexIndex = i;
                    geomIndex = g;
                }
            }
        }
        return {geomIndex: geomIndex, vertexIndex: vertexIndex};
    }

    return {geomIndex, vertexIndex};
}

function getClosesMidPointIndex(baseMap: AzureBaseMap, geom: Polygon|MultiPolygon, midPoints: Position[][], pixel: [number, number], pixelMargin: number): GeomVertexIndex {

    if (midPoints.length === 0){
        return {geomIndex: -1, vertexIndex: -1};
    }

    const position = baseMap.fromPx(pixel);
    const maxOffset = baseMap.fromPx([pixel[0] - pixelMargin, pixel[1] - pixelMargin]);
    const maxDx = Math.abs(position[0] - maxOffset[0]);
    const maxDy = Math.abs(position[1] - maxOffset[1]);

    let vertexIndex = -1;
    let geomIndex = -1;
    let minOffset = maxDy + maxDx;

    if (geom.type == "MultiPolygon") {
        const points = midPoints;
        for (let p = 0; p < points.length; p++) {
            const point = points[p];
            for (let i = 0; i < point.length; i++) {
                const dx = Math.abs(point[i][0] - position[0]);
                const dy = Math.abs(point[i][1] - position[1]);
                const offset = dy + dx;

                if (offset < minOffset) {
                    minOffset = offset;
                    vertexIndex = i;
                    geomIndex = p;
                }
            }
        }
        return {geomIndex: geomIndex, vertexIndex: vertexIndex};
    }

    const points = midPoints;
    for (let i = 0; i < points[0].length; i++) {
        const dx = Math.abs(points[0][i][0] - position[0]);
        const dy = Math.abs(points[0][i][1] - position[1]);
        const offset = dy + dx;
        if (offset < minOffset) {
            minOffset = offset;
            vertexIndex = i;
        }
    }
    return {geomIndex: 0, vertexIndex: vertexIndex};

}

/**
 * Get the Position at the middle between two Position
 * @param line Set of 2 Position
 * @param geodesic Set to true to consider the line as Geodesic.
 */
//TODO Refactor / move method to utils
function getMidPoint(line: Position[], geodesic?: boolean): Position {
    if (line.length != 2) {
        throw new Error("Line must have 2 position!");
    }
    if (geodesic) {
        return math.getPositionsAlongPath(line, 3)[1] as Position
    }
    return [(line[0][0]+line[1][0])/2, (line[0][1]+line[1][1])/2];
}
